// v1.7.0 · CRM · piezas compartidas: llamadas al API, formato, modal, campos y estados
import React, { useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/crm`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
export const api = {
  get: (p, params) => axios.get(`${API}${p}`, { ...h(), params }).then((r) => r.data),
  post: (p, body) => axios.post(`${API}${p}`, body, h()).then((r) => r.data),
  put: (p, body) => axios.put(`${API}${p}`, body, h()).then((r) => r.data),
  patch: (p, body) => axios.patch(`${API}${p}`, body, h()).then((r) => r.data),
  del: (p) => axios.delete(`${API}${p}`, h()).then((r) => r.data),
  blob: (p, params) => axios.get(`${API}${p}`, { ...h(), params, responseType: 'blob' }).then((r) => r.data),
};

export const errMsg = (e, fallback = 'Ocurrió un error') => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x).join(' · ');
  if (d && typeof d === 'object') return d.message || fallback;
  return d || e?.message || fallback;
};

export const money = (n) => `$${Number(n || 0).toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
// Una fecha sola (AAAA-MM-DD) se lee al mediodía para que la zona horaria no la mueva al día anterior
// v1.9.1 · todas las fechas y horas en hora de Ecuador, sin importar la zona del navegador
export const BIZ_TZ = 'America/Guayaquil';
export const fdate = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', timeZone: BIZ_TZ }) : '—');
// v1.7.1 · mismo formato que fdate más la hora («20 oct 2026 · 20:06»)
export const fdatetime = (iso) => (iso ? `${fdate(iso)} · ${new Date(iso).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: BIZ_TZ })}` : '—');
// v1.7.1 · «hoy» según la fecha local del equipo (antes UTC: en Ecuador, después de las 19:00 ya era «mañana»)
export const today = () => new Date().toLocaleDateString('en-CA', { timeZone: BIZ_TZ });

export const STATUS = {
  open: { label: 'Abierta', cls: 'bg-blue-50 text-blue-700 border-blue-200', color: '#2a78d6' },
  won: { label: 'Ganada', cls: 'bg-green-50 text-green-700 border-green-200', color: '#2E7A22' },
  lost: { label: 'Perdida', cls: 'bg-red-50 text-red-700 border-red-200', color: '#e34948' },
  abandoned: { label: 'Abandonada', cls: 'bg-gray-100 text-gray-600 border-gray-300', color: '#8A94A3' },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.open;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${s.cls}`}>{s.label}</span>;
}

export function Tag({ children, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#2F3D8E]/10 px-2 py-0.5 text-[11px] font-medium text-[#2F3D8E]">
      {children}
      {onRemove && <button type="button" onClick={onRemove} aria-label={`Quitar ${children}`} className="hover:text-red-600"><X size={11} /></button>}
    </span>
  );
}

export function Modal({ title, onClose, children, footer, wide = false }) {
  useEffect(() => {
    const k = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={title}
           className={`flex max-h-[90vh] w-full flex-col rounded-2xl bg-white shadow-2xl ${wide ? 'max-w-3xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between border-b px-5 py-3.5">
          <h3 className="text-[15px] font-bold text-[#0F2A3A]">{title}</h3>
          <button onClick={onClose} aria-label="Cerrar" className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#2F3D8E] focus:outline-none focus:ring-2 focus:ring-[#2F3D8E]/20';
export const btn = {
  primary: 'inline-flex items-center gap-1.5 rounded-lg bg-[#2F3D8E] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#25316f] disabled:opacity-50 active:scale-[.98] transition',
  ghost: 'inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 active:scale-[.98] transition',
  danger: 'inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50',
};

/** group: el contenido tiene botones propios (buscador, opciones). Se usa <div> en vez de <label> porque el
 *  <label> repite el clic en su primer botón cuando React cambia el contenido (v1.7.1 · contacto que se borraba). */
export function Field({ label, children, hint, group = false, testId }) {
  const Tag = group ? 'div' : 'label';
  return (
    <Tag className="block text-[13px] font-semibold text-gray-700" data-testid={testId} {...(group ? { role: 'group', 'aria-label': label } : {})}>
      {label}
      <div className="mt-1 font-normal">{children}</div>
      {hint && <span className="mt-1 block text-[11px] font-normal text-gray-500">{hint}</span>}
    </Tag>
  );
}

/** v1.12.1 · Con «solo sus registros» el responsable es siempre el propio usuario: se muestra fijo, sin selector */
export function OwnOwner({ meta, label = 'Responsable' }) {
  return (
    <Field label={label} hint="Con «solo sus registros», lo que crea queda a su nombre.">
      <input className={inputCls} value={meta.me_name || 'Usted'} disabled readOnly data-testid="crm-own-owner" />
    </Field>
  );
}

/** Entrada para un campo personalizado según su tipo */
export function CustomInput({ field, value, onChange }) {
  const t = field.type;
  if (t === 'textarea') return <textarea className={inputCls} rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  if (t === 'select') {
    return (
      <select className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (t === 'multiselect') {
    const vals = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1.5">
        {field.options.map((o) => {
          const on = vals.includes(o);
          return (
            <button key={o} type="button" onClick={() => onChange(on ? vals.filter((x) => x !== o) : [...vals, o])}
                    className={`rounded-full border px-2.5 py-1 text-xs ${on ? 'border-[#2F3D8E] bg-[#2F3D8E] text-white' : 'border-gray-300 bg-white'}`}>{o}</button>
          );
        })}
      </div>
    );
  }
  if (t === 'checkbox') return <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />;
  const type = { number: 'number', date: 'date', email: 'email', phone: 'tel', url: 'url' }[t] || 'text';
  return <input className={inputCls} type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
}

export function customDisplay(field, v) {
  if (v === undefined || v === null || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ');
  if (v === true) return 'Sí';
  if (v === false) return 'No';
  return String(v);
}

export function Empty({ title, children, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
      <div className="text-[15px] font-semibold text-gray-800">{title}</div>
      <div className="max-w-md text-sm text-gray-500">{children}</div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export async function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
